import { SliderInput, Tabs, Tooltip } from '@meitu/saas-ui';
import './image-main-panel.less';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { clientInfo } from '@meitu/pc-bridge';
import ImageEffectAction from '@meitu/saas-editor-core/es/layers/image-layer/utils/image-effect-action';
import { base64Kit } from '@meitu/saas-editor-core/es/utils/base64-kit';
import {
    IImageLayerProps,
    ILayerProps,
    LayerType,
    MaterialIdsType,
    PluginEnum,
} from '@meitu/saas-editor-core/es/version-types';
import { BaseToolbar } from '@meitu/saas-editor-ui/es/components/base-toolbar';
import { DividerLine } from '@meitu/saas-editor-ui/es/components/divider-line';
import { HeaderToolbar } from '@meitu/saas-editor-ui/es/components/header-toolbar';
import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import { useListenActivePickLayerProps } from '@meitu/saas-editor-ui/es/hooks/use-listen-layer-props';
import IconFont from '@meitu/xdesign-icon-font';
import effect5 from '@/assets/effect/effect5.png';
import imageMattingImg from '@/assets/imgs/image-mattiing.png';
import proBadgeImg from '@/assets/imgs/pro-badge.png';
import svipBadgeImg from '@/assets/imgs/svip-badge.png';
import vipBadgeImg from '@/assets/imgs/vip-badge.png';
import useOpenPlugins from '@/editor/collapse/views/more-collapse/hooks/use-open-plugins';
import { events, IMAGE_MATTING_CONFIRM_EVENT, REPLACE_IMAGE_EVENT } from '@/events-bus';
import { usePanelLayerId } from '@/hooks';
import { isEditAIPPTComponent, isEditAIPPTTemplate } from '@/hooks/fetch-template/editor-mode';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { isIntl } from '@/react-process-env';
import { useStoreState } from '@/store';
import { RecordInfo } from '@/store/models/record-model';
import { ICustomParams } from '@/store/models/sdk-model';
import { checkShowSvipCorner, isInCutoutTesting } from '@/utils/ab-test';
import { getLayerType, HandleHbLayersFunc } from '@/utils/stat-track';
import PPTMarkPanel from '../../ppt-mark-panel';
import UploadReplace from '../components/upload-replace/upload-replace';
import ImageConfig from '../image-config-panel/image-config-panel';
import ImageEffectMenu from '../image-effect-menu/image-effect-menu';
import { IImgPanelType } from '../type';

const isAIPPTMark = isEditAIPPTComponent() || isEditAIPPTTemplate();

//HACK:PluginEnum由于在右键菜单里使用，没有包含变清晰和无所改尺寸，这里只能包裹一层

interface IImageMainPanelProps {
    selectPanel: Function;
    isAiPPT: boolean;
    panelType: IImgPanelType | null;
    onImageMatting: (layer: ILayerProps) => Promise<void>;
    onUpdateReplaceImageNum: () => void;
}

const ImageBtnTools = React.memo(
    ({
        layerId,
        isAiPPT,
        statLayerTypeRef,
        selectPanel,
        customParams,
        onImageMatting,
        onUpdateReplaceImageNum,
    }: {
        layerId: string;
        isAiPPT: boolean;
        statLayerTypeRef: { current: number };
        selectPanel: Function;
        customParams: ICustomParams;
        onImageMatting: (layer: ILayerProps) => Promise<void>;
        onUpdateReplaceImageNum: () => void;
    }) => {
        const { $t } = useLocaleStore();
        const { openPlugin } = useOpenPlugins();
        const recordInfo = useStoreState((state) => state.recordModel.recordInfo);
        const isPhoto = layerId ? editor.imageLayer.checkIsPhoto(layerId) : false;
        const [activeLayer, setActiveLayer] = useState<ILayerProps>(editor.stage.getLayer(layerId, true));
        const uploadRef = useRef<any>(null);
        useEffect(() => {
            setActiveLayer(editor.stage.getLayer(layerId, true));
            const handle = editor.stage.onLayerChange(() => {
                setActiveLayer(editor.stage.getLayer(layerId, true));
            }, layerId);
            return () => {
                editor.off(handle);
            };
        }, [layerId]);
        const isAiText = layerId ? editor.imageLayer.checkIsAiText(layerId) : false;
        const firstId = isPhoto ? 4 : isAiText ? 6 : 2;

        // 埋点事件
        const modalShowFrom = useRef<'' | 'click' | 'panel'>('');

        const [showCutoutTooltip, setShowCutoutTooltip] = useState(false);
        const stageType = editor.stage.getType();
        const handleCropImage = useCallback(() => {
            // 裁剪点击埋点
            HandleHbLayersFunc({
                firstId: firstId,
                secondId: 6,
                isClick: true,
                recordId: (recordInfo.id || '').toString(),

                layerType: statLayerTypeRef.current,
                canvas_type: stageType,
            });
            editor.cropImg.open();
            selectPanel(IImgPanelType.CROP);
        }, [firstId]);
        // 很奇怪，加在组件自带的beforeUpload回调上，跟图片编辑器表现行为不一致。。
        // 所以暂时单独写一个click事件
        const beforeUpload = useCallback(() => {
            // 替换埋点
            HandleHbLayersFunc({
                firstId: firstId,
                secondId: 3,
                isClick: true,
                recordId: (recordInfo.id || '').toString(),

                layerType: statLayerTypeRef.current,
                canvas_type: stageType,
            });
        }, [firstId]);

        const handleImageMattingClick = async () => {
            await onImageMatting(activeLayer);
        };

        const handleMask = useCallback(() => {
            if (!layerId) return;
            const activeLayer = editor.stage.getLayer(layerId);
            selectPanel(IImgPanelType.MASK);
            HandleHbLayersFunc({
                firstId: firstId,
                secondId: 40,
                isClick: true,
                recordId: (recordInfo.id || '').toString(),
                layer: activeLayer,
                layerType: statLayerTypeRef.current,
                canvas_type: stageType,
            } as any);
        }, [layerId]);

        const handleOpenPlugin = useCallback((type: string, funcId: number) => {
            openPlugin({ func: type });
            HandleHbLayersFunc({
                firstId: firstId,
                secondId: funcId,
                isClick: true,
                isUse: true,
                recordId: (recordInfo.id || '').toString(),
                layer: activeLayer,
                layerType: statLayerTypeRef.current,
                canvas_type: stageType,
            } as any);
        }, []);

        // 调色
        const handleMixColors = useCallback(() => {
            selectPanel(IImgPanelType.ADJUST);
        }, []);

        useEffect(() => {
            const confirmImageMatting = ({ id }: any) => {
                if (layerId !== id) return;
                if (!layerId) return;
                const layer = editor.stage.getLayer(layerId || '');
                window._stat_track('hb_layers_func_click', {
                    func_id: '2_110',
                });
                HandleHbLayersFunc({
                    firstId: modalShowFrom.current ? 2 : firstId,
                    secondId: modalShowFrom.current === 'click' ? 110 : 38,
                    isUse: true,
                    recordId: (recordInfo.id || '').toString(),
                    layer,
                    layerType: statLayerTypeRef.current,
                    canvas_type: stageType,
                } as any);
            };

            const eventId = events.on(IMAGE_MATTING_CONFIRM_EVENT, confirmImageMatting);
            return () => {
                events.off(eventId);
            };
        }, [firstId]);

        // 替换图片事件
        useEffect(() => {
            const handleReplace = () => {
                beforeUpload();
                if (!layerId) return;
                uploadRef.current?.open();
                if (isIntl) {
                    modalShowFrom.current = 'panel';
                }
            };
            events.on(REPLACE_IMAGE_EVENT, handleReplace);
            return () => {
                events.off(REPLACE_IMAGE_EVENT, handleReplace);
            };
        }, [layerId]);

        return (
            <div className="c-images-panel-main__btns">
                <div className="c-images-panel-main__btn" onClick={handleCropImage}>
                    <IconFont name="CropBold" fontSize={16} />
                    {$t((v) => v.panel.imagePanel.imageCrop)}
                </div>

                <div className="c-images-panel-main__btn" onClick={handleMixColors}>
                    <IconFont name="PaletteBold" fontSize={16} />
                    {$t((v) => v.panel.imagePanel.imageAdjust)}
                </div>

                <Tooltip placement="left" title={$t((v) => v.panel.imagePanel.removeBgTip)} open={showCutoutTooltip}>
                    <div
                        className="c-images-panel-main__btn"
                        onClick={() => {
                            handleImageMattingClick();
                            setShowCutoutTooltip(false);
                        }}
                    >
                        <IconFont name="CutOutBold" fontSize={16} />
                        {$t((v) => v.panel.imagePanel.imageCutout)}
                        {/* SDK内隐藏抠图角标 */}
                        {customParams.hideMattingVip ? null : isInCutoutTesting() ? (
                            process.env.REACT_APP_IS_INTL === '1' ? (
                                <img className="intl-badge" src={proBadgeImg} alt="" />
                            ) : checkShowSvipCorner() ? (
                                <img src={clientInfo.isClient ? svipBadgeImg : vipBadgeImg} alt="" />
                            ) : null
                        ) : (
                            <img src={imageMattingImg} alt="" />
                        )}
                    </div>
                </Tooltip>

                <div className="c-images-panel-main__btn" onClick={handleMask}>
                    <IconFont name="MaskBold" fontSize={16} />
                    {$t((v) => v.panel.imagePanel.imageMask)}
                </div>
                <div
                    className="c-images-panel-main__btn"
                    onClick={() => handleOpenPlugin(PluginEnum.AI_EXPEND_IMAGE, 170)}
                >
                    <IconFont name="AiImageExpandBold" fontSize={16} />
                    {$t((v) => v.panel.imagePanel.imageAIExpand)}
                </div>

                <div
                    className="c-images-panel-main__btn"
                    onClick={() => handleOpenPlugin(PluginEnum.AI_MODIFY_IMAGE, 170)}
                >
                    <IconFont name="ApplyBold" fontSize={16} />
                    {$t((v) => v.panel.imagePanel.imageModify)}
                </div>
                <UploadReplace ref={uploadRef} layerId={layerId} onUpdateReplaceImageNum={onUpdateReplaceImageNum} />
            </div>
        );
    },
);

const ImageOpacity = React.memo(
    ({
        is_search,
        id: layerId,
        recordInfo,
        statLayerTypeRef,
    }: {
        is_search: string;
        id: string;
        recordInfo: RecordInfo;
        statLayerTypeRef: { current: number };
    }) => {
        const { $t } = useLocaleStore();

        const originOpacity = useRef<number | null>(null);
        const activeLayer = editor.stage.getLayer(layerId || '');
        const isPhoto = layerId ? editor.imageLayer.checkIsPhoto(layerId) : false;
        const isAiText = layerId ? editor.imageLayer.checkIsAiText(layerId) : false;
        const firstId = isPhoto ? 4 : isAiText ? 6 : 2;
        const materialIds = isPhoto
            ? activeLayer?.materialIds?.[MaterialIdsType.PHOTO_MATERIAL]
            : activeLayer?.materialIds?.[MaterialIdsType.IMAGE_MATERIAL];
        const stageType = editor.stage.getType();
        const opacity = useListenActivePickLayerProps(editor, (layer) => {
            if (!layer) return null;
            const { opacity } = layer;
            return opacity;
        });

        return (
            <div className="c-images-panel-main__slide">
                <LabelAction title={$t((v) => v.panel.imagePanel.opacity)}>
                    <div className="text-action__item">
                        <SliderInput
                            value={parseInt(`${(opacity || 0) * 100}`)}
                            showStepBtn={true}
                            max={100}
                            min={0}
                            type="horizontal"
                            onChange={(val: number) => {
                                if (!layerId) return;
                                const layer = editor.stage.getLayer(layerId || '');
                                if (originOpacity.current === null) {
                                    originOpacity.current = opacity;
                                }

                                editor.imageLayer.setOpacity(layer.id, val / 100);
                            }}
                            onAfterChange={(val: number) => {
                                if (!layerId) return;
                                const layer = editor.stage.getLayer(layerId || '');
                                editor.imageLayer.setOpacity(layer.id, val / 100);
                                originOpacity.current = null;

                                // // 图层功能点击埋点
                                HandleHbLayersFunc({
                                    firstId: firstId,
                                    secondId: 21,
                                    isClick: true,
                                    isUse: true,
                                    layerType: statLayerTypeRef.current,
                                    recordId: (recordInfo.id || '').toString(),
                                    layer,
                                    canvas_type: stageType,
                                } as any);

                                window._stat_track('hb_picture_edit_yes_data', {
                                    样式不透明度: val / 100,
                                    is_search,
                                    分类: layer?.category,
                                    素材: materialIds?.join(','),
                                });
                            }}
                        />
                    </div>
                </LabelAction>
            </div>
        );
    },
);

const pptMarkTabsList = [
    {
        name: '图片',
        tabKey: 'bg',
    },
    {
        name: 'ppt打标',
        tabKey: 'pptMark',
    },
];

const RenderTabs: React.FC<{
    showMaskContent: boolean;
    setShowMaskContent: (flag: boolean) => void;
    isPPT?: boolean;
}> = ({ isPPT = false, setShowMaskContent }) => {
    const [tabIndex, setTabIndex] = useState(1);
    const handleTabClick = (key: string) => {
        setTabIndex(key === 'bg' ? 1 : 2);
        setShowMaskContent(key !== 'bg');
    };

    return (
        <div style={{ padding: '0 var(--gap-lg)' }}>
            <Tabs
                showContent={false}
                data={isPPT ? pptMarkTabsList : []}
                tabsWidth="228px"
                tabsHeight="34px"
                handleTabClick={handleTabClick}
                activeIndex={tabIndex}
            />
        </div>
    );
};

const ImageMainPanel: React.FC<IImageMainPanelProps> = ({
    selectPanel,
    isAiPPT,
    onImageMatting,
    onUpdateReplaceImageNum,
}) => {
    const { $t } = useLocaleStore();
    const statLayerTypeRef = useRef<number>(0);

    const layerId = usePanelLayerId();
    const [showMaskContent, setShowMaskContent] = useState(false);

    const isPhoto = layerId ? editor.imageLayer.checkIsPhoto(layerId) : false;
    const isAiText = layerId ? editor.imageLayer.checkIsAiText(layerId) : false;
    const firstId = isPhoto ? 4 : isAiText ? 6 : 2;

    const getMaterialIds = () => {
        const layer = editor.stage.getLayer(layerId || '');
        return layer?.materialIds?.[MaterialIdsType.IMAGE_MATERIAL];
    };
    const recordInfo = useStoreState((state) => state.recordModel.recordInfo);

    const is_search = useStoreState((state) => state.searchModel.is_search);
    const customParams = useStoreState((state) => state.sdkModel.customParams);

    const url = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;
        const { url } = layer.data as IImageLayerProps;
        return url;
    });

    const maskURL = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;
        const { maskURL } = layer.data as IImageLayerProps;
        return maskURL;
    });

    const [src, setSrc] = useState<string>('');
    const stageType = editor.stage.getType();
    const [imgEffectPreview, setImgEffectPreview] = useState('');

    useEffect(() => {
        const fn = async () => {
            if (!url) return;
            setSrc(await base64Kit.getLocalUrl(url));
        };
        fn();
    }, [url]);

    useEffect(() => {
        if (!layerId) return;
        const activeLayer = editor.stage.getLayer(layerId);

        if (activeLayer) {
            const res = getLayerType(activeLayer);
            statLayerTypeRef.current = res.layerType;
        }
    }, [layerId]);

    useEffect(() => {
        if (!layerId) return;
        const activeLayer = editor.stage.getLayer(layerId);
        if (!activeLayer || activeLayer.layerType !== LayerType.IMAGE_LAYER) {
            setImgEffectPreview('');
            return;
        }

        const { data } = activeLayer;
        const { effect } = data as IImageLayerProps;

        if (!effect || ImageEffectAction.isEmptyEffect(data as IImageLayerProps)) {
            setImgEffectPreview('');
            return;
        }

        setImgEffectPreview(effect5);
    }, [layerId]);

    // 删除图层
    const onDeleteCallback = useCallback(() => {
        if (!layerId) return;
        const activeLayer = editor.stage.getLayer(layerId);
        // 图层功能点击埋点
        HandleHbLayersFunc({
            firstId: firstId,
            secondId: 25,
            isClick: true,
            isUse: true,
            recordId: (recordInfo.id || '').toString(),
            activeLayer,
            layerType: statLayerTypeRef.current,
            canvas_type: stageType,
        } as any);
        window._stat_track('hb_picture_edit_yes_data', {
            删除: '是',
            is_search,
            分类: activeLayer?.category,
            素材: getMaterialIds()?.join(','),
        });
    }, [firstId]);

    useEffect(() => {
        const deleteLayer = () => {
            const activeLayer = editor.active.getPick(true);
            if (activeLayer?.layerType === LayerType.IMAGE_LAYER) {
                onDeleteCallback();
            }
        };

        const id = editor.onDelete(deleteLayer);

        return () => {
            setTimeout(() => {
                editor.off(id);
            }, 0);
        };
    }, []);

    const handleAICallback = (func?: string) => {
        // console.log('handleAICallback', func);
    };

    if (!layerId) return null;
    return (
        <div className="c-images-panel-main__wrap">
            {isAIPPTMark ? (
                <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                    <RenderTabs
                        showMaskContent={showMaskContent}
                        setShowMaskContent={setShowMaskContent}
                        isPPT={isAIPPTMark}
                    />
                </div>
            ) : null}

            <div
                style={{
                    display: `${isAIPPTMark && showMaskContent ? 'none' : 'block'}`,
                }}
            >
                <HeaderToolbar
                    id={layerId}
                    editor={editor}
                    events={events}
                    style={{ padding: 16 }}
                    onAI={handleAICallback}
                />
                <ImageConfig layerId={layerId} />
                <DividerLine />
                <ImageBtnTools
                    {...{
                        layerId: layerId,
                        isAiPPT,
                        statLayerTypeRef,
                        selectPanel,
                        customParams,
                        onImageMatting,
                        onUpdateReplaceImageNum,
                    }}
                />

                <DividerLine />

                <div className="c-images-panel-main__menu">
                    <ImageEffectMenu
                        layerId={layerId}
                        selectPanel={() => {
                            if (!layerId) return;
                            const activeLayer = editor.stage.getLayer(layerId);
                            HandleHbLayersFunc({
                                firstId: firstId,
                                secondId: 47,
                                isClick: true,
                                recordId: (recordInfo.id || '').toString(),
                                activeLayer,
                                layerType: statLayerTypeRef.current,
                                canvas_type: stageType,
                            } as any);
                            selectPanel(IImgPanelType.EFFECT);
                        }}
                        imgEffectPreview={imgEffectPreview}
                    />
                </div>
                <ImageOpacity
                    {...{
                        id: layerId,
                        recordInfo,
                        is_search: is_search,

                        statLayerTypeRef,
                    }}
                />
                <DividerLine />

                <BaseToolbar id={layerId} editor={editor} style={{ padding: '0 16px 16px' }} />
            </div>
            {isAIPPTMark && showMaskContent ? <PPTMarkPanel editor={editor} key={layerId} /> : null}
        </div>
    );
};

export default React.memo(ImageMainPanel);
